import { useMsal } from "@azure/msal-react";
import {
    CommandBar, IButtonProps,
    ICommandBarItemProps,
    IContextualMenuItem,
    IconButton,
    MessageBar, MessageBarType,
    Text,
    useTheme
} from "@fluentui/react";
import { RootState } from "app/store";
import saveAs from "file-saver";
import BreadcrumbNavigation from "global/Application/BreadcrumbNavigation";
import { useApplication } from "global/Application/Context";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdateGroupConfigurationShowInActives, notificationRemove, toggleCommentStatus } from "global/Reducer/dataSlice";
import { IComponentContextMenu } from "model/Application";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import {
    EGroupConfigurationStatus as FAM_EGroupConfigurationStatus
} from "model/FlexibleAttributeModel";
import { IGLWindow } from "model/GroupLibraryWindow";
import { useEffect, useMemo, useRef, useState } from "react";
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { useHistory } from "react-router";
import CompareContextMenuRadialOverlay from "views/BlueRelationship/Compare/CompareContextMenuRadialOverlay";
import { EViewType, emitCustomEvent, fadeIn } from "../../../global/Application";
import { ESaveType, SaveAction, loadSnapshotAction } from '../../../global/Application/dataActions';
import {
    ELoadStatus,
    setConcurrencyConfirmationModal, setIsNavPinned, setProcessing, setViewType, updateDebug
} from "../../../global/Reducer/statusSlice";
import CommentPanel from "../../../views/BlueRelationship/CommentPanel";
import { ConfirmationDialog } from "../../controls/ConfirmationDialog";
import CommentReviewPanel, { buildDataEntryRoute, ECommentReviewPanelViewType } from "views/BlueRelationship/Compare/CommentReviewPanel/CommentReviewPanel";
import { setStatusHighlightField } from "../../../global/Reducer/statusSlice";
import { WithRecycle } from "../../../global/Application/dataActions";


const overflowProps: IButtonProps = { ariaLabel: 'More commands' };


interface IEditorNavbarProps extends TComponentConnectedProps, IComponentContextMenu {
    hideShowDeactivated?: boolean;
    unStickyNavbar?: boolean;
    hideSave?: boolean;
    isChecklistApp?: boolean;
    isCompareApp?: boolean;
    isMergeApp?: boolean;
    isUnderwritingReviewApp?: boolean;
}

const EditorNavbar = (props: IEditorNavbarProps) => {
    const history = useHistory();

    const authentication = useAuthentication();
    const appContext = useApplication();

    const [commentsPanel, setCommentsPanel] = useState(false);
    const [contextRadialNavIsHidden, setContextRadialNavIsHidden] = useState(true);

    const dispatch = useDispatch();
    const theme = useTheme();

    const refStickyBar = useRef(null);
    const [sticky, setSticky] = useState(false);

    const isCheckList = props.state.router.location.pathname.indexOf('/checklist') > -1;
    const isPharmacy = props.state.router.location.pathname.indexOf('/pharmacy') > -1;

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const canModify = props.state?.status?.viewType === EViewType.Modify;

    let menuOverflow: ICommandBarItemProps[] = [];
    const menuFar: ICommandBarItemProps[] = [];

    let menu: ICommandBarItemProps[] = [];
    const subMenuSave: IContextualMenuItem[] = [];

    let isConfirm = false;

    let submitAsUnderWritingReviewComplete = false;
    if (
        props.state.data.json.changeType?.value === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["PSA Change(808900012)"].value ||
        props.state.data.json.changeType?.value === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Administrative Change(808900003)"].value
    ) {
        submitAsUnderWritingReviewComplete = true;
    }

    const isPowerUser = authentication.authorizations?.roles['BCBS - Power User'];
    const isReadOnlyPowerUser = authentication.authorizations?.roles['BCBS - Read Only Power User - BDS'];

    function onScroll(_: any) {
        if (!sticky && window.pageYOffset >= 50 && props.state.status.isNavPinned)
            // if (!isCheckList)
            if (!props.unStickyNavbar)
                setSticky(true);

        if (sticky && window.pageYOffset < 50)
            setSticky(false);
    };

    useEffect(() => {
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [sticky, props.state.status.isNavPinned]);

    useEffect(() => {
        if (!props.state.status.isNavPinned)
            setSticky(false);
    }, [props.state.status.isNavPinned]);

    useEffect(() => {
        onScroll(null);
    }, []);

    // Save on Ctrl+S or Cmd+S
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {

            if (e.ctrlKey && e.altKey && !e.shiftKey) {
                e.preventDefault();
                setContextRadialNavIsHidden(t => !t);
                return;
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [setContextRadialNavIsHidden]);

    const styleStickyBar = useMemo(() => {
        if (!sticky)
            return {
                transition: '0.1s',
                backgroundColor: theme.semanticColors.bodyBackground,
            } as React.CSSProperties;

        const style: React.CSSProperties = {
            position: 'fixed',
            top: '0',
            width: '100%',
            zIndex: 100000,
            borderBottom: '1px solid gray',
            transition: '0.1s',
            backgroundColor: theme.semanticColors.bodyBackground,
        };

        return style;
    }, [sticky, theme]);

    // For Admin-Change/PSA-Change we ought to display 'Confirm'
    // Updates to Submit Process for Admin Changes & PSA Changes
    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/250052
    isConfirm = isConfirm || submitAsUnderWritingReviewComplete;

    // For cancellation we ought to display 'Confirm'
    isConfirm = isConfirm || props.state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus['Transferred to Other LOB in Progress'] ||
        props.state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus['Cancellation in Progress'];

    menuFar.push({
        key: 'far.share',
        text: 'Share',
        cacheKey: 'myCacheKey', // changing this key will invalidate this item's cache
        iconProps: { iconName: 'Share' },
        subMenuProps: {
            items: [
                {
                    key: 'emailMessage',
                    text: 'Email Link',
                    iconProps: { iconName: 'Mail' },
                    onClick: () => {
                        const subject = `Group Library - ${props.state.data.snapshot.bcbs_name}`;
                        const body = `Regarding ${window.location.href}\r\n`;
                        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                    }
                },
                {
                    key: 'calendarEvent',
                    text: 'Copy Page Link',
                    iconProps: { iconName: 'PageLink' },
                    onClick: () => {
                        navigator.clipboard.writeText(window.location.href);
                    }
                },
            ],
        },
    },
        {
            key: 'pin',
            text: '',
            iconProps: { iconName: props.state.status.isNavPinned ? 'PinnedSolid' : 'Pin' },
            onClick: (event, item) => {
                dispatch(setIsNavPinned());
            }
        }

    );

    menu.push({
        key: 'main.back',
        text: 'Back',
        iconProps: { iconName: 'Back' },
        onClick: () => {
            history.goBack();
        }
    });

    if (canModify && !isCheckList && !isPharmacy && !props.hideSave) {
        subMenuSave.push({
            key: 'main.save.save',
            text: 'Save',
            iconProps: { iconName: 'BrowserTabScreenshot' },
            onClick: () => {
                appContext.SaveAsync({
                    saveType: ESaveType.EManual,
                });
            },
        });

        if (props.state.data.config.bcbs_config_release?.statuscode !== IXrmOptions.bcbs_config_release.statuscode["Published(2)"].value) {
            subMenuSave.push({
                key: 'main.save.update',
                text: 'Update Release',
                iconProps: { iconName: 'Sync' },
                onClick: () => {
                    dispatch(
                        SaveAction(
                            dataSource,
                            history,
                            {
                                saveType: ESaveType.EUpgradeRelease,
                                onSaveStatusScreen: true,
                            }
                        )
                    );
                },
            });
        }

        subMenuSave.push({
            key: 'main.save.submit',
            text: isConfirm
                ? 'Confirm'
                : 'Submit',
            iconProps: { iconName: 'MedalSolid' },
            onClick: (event, item) => {
                appContext.SaveAsync({
                    saveType: ESaveType.ESubmit
                });
            }
        });

        if (props.state.status.debug.admin)
            subMenuSave.push({
                key: 'main.save.submit.bypass',
                text: 'Confirm - Bypass Underwriting and Task Generation',
                iconProps: { iconName: 'MedalSolid' },
                onClick: (event, item) => {
                    appContext.SaveAsync({
                        saveType: ESaveType.ESubmit,
                        onSaveBypassUnderwritingTaskGeneration: true,
                    });
                }
            });

        if (!props.hideSave)
            menu.push({
                key: 'main.save',
                text: 'Save',
                iconProps: { iconName: 'Save' },
                disabled: props.state.status.dataSavingStatus === ELoadStatus.criticalSaving,
                subMenuProps: {
                    items: subMenuSave
                }
            });
    }

    if (props?.contextMenu?.items)
        menu.push(...props.contextMenu.items);

    if (props.state.status.debug.admin ||
        (authentication.authorizations?.canEdit && props.state.data.snapshot?.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value && !props.isUnderwritingReviewApp)
    )
        menu.push({
            key: 'main.comments',
            text: 'View Comments',
            iconProps: { iconName: 'Comment' },
            cacheKey: 'myCacheKey',
            onClick: () => { setCommentsPanel(!commentsPanel) }
        });

    if (!isCheckList && !props.hideShowDeactivated && !isPharmacy)
        menu.push({
            key: 'main.showDeactivated',
            text: 'Show Deactivated',
            iconProps: {
                iconName: props.state?.data?.json?.showInActives?.value ? 'RedEye' : 'Hide',
                styles: {
                }
            },
            disabled: props.state.status.dataSavingStatus === ELoadStatus.saving,
            buttonStyles: {
                root: {
                    backgroundColor: !props?.state?.data?.json?.showInActives?.value ? undefined : theme.semanticColors.warningBackground,
                }
            },
            onClick: () => {
                dispatch(addUpdateGroupConfigurationShowInActives(
                    {
                        type: 'bool',
                        value: !props?.state?.data?.json?.showInActives?.value
                    }
                ));

            }
        });

    let myLastSaveDate: Date = new Date();
    let lastSaveDate: Date = new Date();

    if (props.state?.status?.lastSaved) {
        myLastSaveDate = new Date(props.state?.status?.lastSaved);
    } else if (props.state?.data?.snapshot?.modifiedon) {
        myLastSaveDate = new Date(props.state?.data?.snapshot?.modifiedon);
    }

    if (props.state.status.concurrencyConfirmationModal?.modifiedon) {
        lastSaveDate = new Date(props.state.status.concurrencyConfirmationModal?.modifiedon);
    }

    const adminMenu = () => {

        const debugSubMenu: IContextualMenuItem[] = [];

        if (isPowerUser)
            debugSubMenu.push({
                key: 'main.admin.viewType',
                text: props.state.status.viewType === EViewType.ViewOnly
                    ? '✏️ Switch to Edit'
                    : '👁️ Switch to View',
                onClick: (event, item) => {
                    dispatch(updateDebug({ autoSaveOff: true }));

                    if (props.state.status.viewType === EViewType.ViewOnly)
                        dispatch(setViewType(EViewType.Modify));

                    else
                        dispatch(setViewType(EViewType.ViewOnly));

                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'main.admin.liveload',
                text: props.state.status.debug.liveload
                    ? '🟢 Reload with live configuration'
                    : '⚫ Reload with live configuration',
                onClick: (event, item) => {
                    dispatch(updateDebug({ liveload: true }));
                    dispatch(updateDebug({ autoSaveOff: true }));
                    dispatch(loadSnapshotAction(
                        dataSource,
                        props.state.data.account?.accountid!,
                        {
                            primarySnapshotId: props.state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                        }
                    ));
                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'main.admin.diagnosis',
                text: props.state.status.debug.diagnosis
                    ? '🟢 Diagnosis'
                    : '⚫ Diagnosis',
                onClick: (event, item) => {
                    dispatch(updateDebug({ diagnosis: !props.state.status.debug.diagnosis }));
                    dispatch(updateDebug({ autoSaveOff: true }));
                }
            });


        if (isPowerUser)
            debugSubMenu.push({
                key: 'main.admin.adminOverride',
                text: props.state.status.debug.admin
                    ? '🟢 Admin override'
                    : '⚫ Admin override',
                onClick: (event, item) => {
                    dispatch(updateDebug({ admin: !props.state.status.debug.admin }));
                    dispatch(updateDebug({ autoSaveOff: true }));
                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'main.admin.autoSaveOff',
                text: props.state.status.debug.autoSaveOff
                    ? '🟢 Autosave is off'
                    : '⚫ Autosave is on',
                onClick: (event, item) => {
                    dispatch(updateDebug({ autoSaveOff: !props.state.status.debug.autoSaveOff }));
                }
            });

        if (isPowerUser || isReadOnlyPowerUser)
            debugSubMenu.push({
                key: 'main.admin.downloadDataModel',
                text: '🌐 Download Data Model',
                onClick: (event, item) => {
                    (async () => {
                        try {

                            dispatch(setProcessing(true));
                            const response = await dataSource.getGenerateDataDictionaryExtract();
                            saveAs(response.blob, response.fileName);

                        } catch (error) {
                            // lets create a new one is payload is not providing one
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });

                        } finally {
                            dispatch(setProcessing(false));
                        }
                    })();
                }
            });

        return ({
            key: 'main.admin',
            text: 'Admin',
            iconProps: { iconName: 'Settings' },
            disabled: props.state.status.dataSavingStatus === ELoadStatus.criticalSaving,
            subMenuProps: {
                items: debugSubMenu
            }
        } as ICommandBarItemProps);
    }

    const onRenderNotificationBar = () => {
        const elements: JSX.Element[] = [];

        if (props.state.data.json.showInActives?.value)
            elements.push(
                <MessageBar key={"notification.showInActives"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Showing only deactivated records. All records below are currently deactivated.
                </MessageBar>
            )

        if (props.state.status.debug.liveload)
            elements.push(
                <MessageBar key={"notification.liveload"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    You are currently live loading the configuration.
                </MessageBar>
            )

        if (props.state.status.debug.admin)
            elements.push(
                <MessageBar key={"notification.admin"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Admin mode has been enabled.
                </MessageBar>
            )

        if (props.state.status.debug.developer)
            elements.push(
                <MessageBar key={"notification.admin"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Developer mode has been enabled.
                </MessageBar>
            )

        if (props.state.status.debug.noVisibility)
            elements.push(
                <MessageBar key={"notification.noVisibility"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Visibility loading has been disabled.
                </MessageBar>
            )

        if (props.state.data.json.MNIL?.value)
            elements.push(
                <MessageBar key={"notification.MNIL"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Showing fields for MNIL.
                </MessageBar>
            )

        if (((window as IGLWindow).preview))
            elements.push(
                <MessageBar key={"notification.preview"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Preview Mode has been enabled
                </MessageBar>
            )

        props.state.data.notifications?.forEach((notification, index) => {
            elements.push(
                <MessageBar
                    key={notification.id}
                    className={fadeIn}
                    messageBarType={MessageBarType.warning}
                    onDismiss={(ev) => {
                        dispatch(notificationRemove(notification.id));
                    }}
                    dismissButtonAriaLabel="Close"
                >
                    <Text style={{ whiteSpace: 'pre-line' }}>
                        {notification.description}
                    </Text>
                </MessageBar>
            )
        });

        if (elements.length > 0)
            return <div key={"notification.host"} style={{ borderTop: '1px solid gray' }}>
                {elements}
            </div>;
        else
            return <></>;

    }

    if (isPowerUser || isReadOnlyPowerUser) {
        menu.push(adminMenu());
    }

    const renderButtonQuickNavBookmark = () => {
        if (props.state.status.isNavPinned)
            return null;

        return <IconButton
            iconProps={{ iconName: 'Pin' }}
            title="Quick Navigation (Ctrl + Shift)"
            ariaLabel="Quick Navigation (Ctrl + Shift)"
            onClick={() => dispatch(setIsNavPinned())}
            styles={{
                icon: {
                },
                root: {
                    zIndex: 1000,
                    backgroundColor: theme.palette.white,
                    border: '1px solid #20202020',
                    borderBottomLeftRadius: '50px',
                    borderTopLeftRadius: '50px',
                    position: 'fixed',
                    top: '55px',
                    right: '0px',
                    ':hover': {
                        background: theme.palette.themeDark,
                        color: theme.palette.white,
                    },
                }
            }}
        />
    }

    const newComment = props.state?.data?.json?.comments?.[0]?.location?.type !== undefined;

    return <>
        <div
            style={{
                height: sticky ? `${(refStickyBar?.current as any)?.offsetHeight}px` : '0px',
                width: '100%',
            }}
        >
        </div>
        {renderButtonQuickNavBookmark()}
        <div style={styleStickyBar} ref={refStickyBar}>
            <CommandBar
                key={"commandBar"}
                styles={{ root: { backgroundColor: theme.palette.white, position: 'sticky', top: '10px' } }}
                items={menu}
                overflowItems={menuOverflow}
                overflowButtonProps={overflowProps}
                farItems={menuFar}
            />
            <div key={"breadcrumb"} style={{ paddingLeft: '44px', paddingRight: '44px' }}>
                <BreadcrumbNavigation />
            </div>

            {onRenderNotificationBar()}
        </div>

        {
            /* TODO move outside of navbar and refactor comments panel */
            props.state.data.snapshot?.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value && !newComment && (
                <CommentPanel
                    isOpen={commentsPanel}
                    dismissPanel={() => { setCommentsPanel(!commentsPanel) }}
                />
            )
        }

        {
            /* TODO move outside of navbar and refactor comments panel */
            props.state.data.snapshot?.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value && newComment && (
                <CommentReviewPanel
                    viewType={ECommentReviewPanelViewType.SidePanel}
                    comments={props.state?.data?.json?.comments}
                    isOpen={commentsPanel}
                    dismissPanel={() => { setCommentsPanel(!commentsPanel) }}
                    onToggleResolved={(comment) => {
                        dispatch(toggleCommentStatus(comment))
                    }}
                    onNavigate={(rootLocation) => {
                        setCommentsPanel(false);

                        const basePath = `/records/blueRelationship/${props.state.data.account?.accountid}/groupConfiguration/${props.state.data.snapshot.bcbs_groupconfigurationsnapshotid}`;
                        const { path, fieldId } = buildDataEntryRoute(rootLocation, basePath);

                        if (fieldId)
                            dispatch(setStatusHighlightField(fieldId));

                        dispatch(
                            WithRecycle(() => {
                                history.push({ pathname: path, search: "?navigation=pending" });
                            }, 100)
                        );
                    }}
                />
            )
        }

        <CompareContextMenuRadialOverlay
            isHidden={contextRadialNavIsHidden}
            contextMenuItems={menu}
            onDismiss={() => setContextRadialNavIsHidden(true)}
        />

        {props.state.status.concurrencyConfirmationModal &&
            <ConfirmationDialog
                dialogContentProps={{
                    title: "Concurrency Conflict"
                }}
                onClose={() => {
                    dispatch(setConcurrencyConfirmationModal(null));
                    dispatch(setViewType(EViewType.ViewOnly));
                }}

                onContinue={async () => {
                    // //todo Armen:  make new snapshot from current state data store, keep original parent snapshot

                    // // TODO WARN* there is a duplicate of this function, that must be refactored
                    // dispatch(setConcurrencyConfirmationModal(null));
                    // const t = await SaveAsync(dispatch, dataSource, props.state, { saveType: ESaveType.EConcurrencyConflict, onSaveStatusScreen: true });

                    // history.push('/finished');
                    // window.open(`/records/blueRelationship/${t.snapshot?._bcbs_groupid_value}/groupConfiguration/${t.snapshot?.bcbs_groupconfigurationsnapshotid}`);

                    // //not sure about the behavior we want to have but save has dispatch to update status.snapshot with latest
                    // //but the function may complete prior to dispatch cycle
                }}
                cancelText="Cancel"
            >
                <p>
                    This configuration has been updated by {props.state.status.concurrencyConfirmationModal?.fullname}. Please reload to see the latest changes to apply your modifications.
                </p>
            </ConfirmationDialog>
        }
    </>
};

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connect(mapStateToProps)(EditorNavbar)